import { useCallback, useEffect, useState } from 'react';
import {
  UserPlus, Trash2, Power, Loader2, ShieldCheck, Crown, User as UserIcon,
  SlidersHorizontal, LogOut, AlertTriangle, CheckCircle2, XCircle, Plus,
} from 'lucide-react';
import { api, ApiError } from '../api/client.js';

/**
 * /users — Users & Access admin (BUILD-QUEUE §8b).
 *
 * List / add / change-role / deactivate·reactivate / delete panel users. All
 * mutations are enforced + audit-logged on the backend (this UI only reflects
 * the guardrails). Adding a user sends NO invite — they simply sign in with
 * Google and their access works.
 */

const ROLE_LABELS = { owner: 'Owner', admin: 'Admin', member: 'Member' };
const FEATURE_LABELS = {
  sites: 'Sites', servers: 'Servers', cloud: 'Cloud Servers', storage: 'Object Storage',
  dns: 'DNS Manager', tools: 'Tools', integrations: 'Integrations', eachange: 'Azure Manager',
  change: 'Oracle IP changer', sponsors: 'Sponsor audit',
  deploypanel: 'Deploy panel',
};

// Friendly copy for the backend's error codes.
const ERRORS = {
  last_owner: 'There must always be at least one active owner — this is the last one.',
  last_manager_self: 'You can’t demote yourself — you’re the only active admin/owner left.',
  cannot_delete_self: 'You can’t delete your own account.',
  cannot_deactivate_self: 'You can’t deactivate your own account.',
  only_owner_can_manage_owners: 'Only an owner can manage owner accounts.',
  email_exists: 'A user with that email already exists.',
  invalid_email: 'That doesn’t look like a valid email address.',
  invalid_role: 'Invalid role.',
  confirm_email_mismatch: 'The typed email didn’t match — nothing was deleted.',
  forbidden: 'You don’t have permission to manage users.',
  not_a_member: 'Owners and admins already have every resource.',
  resource_not_found: 'That resource no longer exists — reload the page.',
  cannot_list_resources: 'The resource list could not be read right now (vps1?) — try again.',
  no_revoke_target_configured: 'No gated app is wired up for revoke on this panel (missing gate secret).',
};
function errMsg(err) {
  const code = err instanceof ApiError ? err.body?.error ?? err.message : 'network_error';
  return ERRORS[code] ?? code;
}

export function Users() {
  const [data, setData] = useState(null); // { users, me }
  const [error, setError] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await api('/api/users'));
      setError(null);
    } catch (err) {
      setError(errMsg(err));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const me = data?.me;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm text-t3 max-w-2xl">
            Who can sign in to the panel, and with what role. Owners and admins
            have full access; members get per-feature access (configured later).
            Adding a user sends <span className="font-medium">no invite</span> — they
            just sign in with Google and it works.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowAdd((v) => !v)}
          className="shrink-0 inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-blue text-white hover:bg-blue-deep"
        >
          <UserPlus className="w-4 h-4" /> Add user
        </button>
      </div>

      {error && (
        <div className="bg-err-bg border border-err-bd text-err text-sm rounded-md px-4 py-3">
          {error}
        </div>
      )}

      {showAdd && me && (
        <AddUserForm me={me} onCreated={() => { setShowAdd(false); load(); }} />
      )}

      {data === null ? (
        <p className="text-sm text-t3">Loading…</p>
      ) : (
        <div className="bg-white border border-bd rounded-lg overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-t3 border-b border-bd">
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Last login</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.users.map((u) => (
                <UserRow
                  key={u.id}
                  user={u}
                  me={me}
                  features={data.features ?? []}
                  onChanged={load}
                  onError={setError}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {me?.role === 'owner' && <RevokeSessionsCard />}
    </div>
  );
}

/**
 * §15 — "Revoke all app sessions". Owner-only (the backend enforces it too).
 *
 * Removing someone's permission above does NOT log them out of the gated apps:
 * their gate cookie lives 8 hours and is never re-checked. This is the only
 * thing that ends those sessions, which is why it sits on this page.
 */
function RevokeSessionsCard() {
  const [targets, setTargets] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState(null); // { ok, warning, results } | { error }

  useEffect(() => {
    let alive = true;
    api('/api/sessions/targets')
      .then((d) => { if (alive) setTargets(d.targets); })
      .catch(() => { if (alive) setTargets([]); });
    return () => { alive = false; };
  }, []);

  const configured = (targets ?? []).filter((t) => t.configured);

  async function revoke() {
    setBusy(true);
    setOutcome(null);
    try {
      setOutcome(await api('/api/sessions/revoke', { method: 'POST' }));
    } catch (err) {
      setOutcome({ error: errMsg(err) });
    }
    setBusy(false);
    setConfirming(false);
  }

  if (targets === null) return null;

  return (
    <div className="bg-white border border-bd rounded-lg p-5 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-sm font-semibold text-t1">
            <LogOut className="w-4 h-4 text-t3" /> Revoke all app sessions
          </div>
          <p className="text-sm text-t3 max-w-2xl">
            Removing a permission above stops the <span className="font-medium">next</span> hand-off,
            but it does not end a session already open in someone’s browser — those
            live up to 8 hours. This ends them all, immediately, for{' '}
            {configured.length > 0
              ? configured.map((t) => t.label).join(', ')
              : 'the gated apps'}.
          </p>
        </div>
        <button
          type="button"
          disabled={busy || configured.length === 0}
          onClick={() => { setOutcome(null); setConfirming(true); }}
          className="shrink-0 inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-err-bd text-err hover:bg-err-bg disabled:opacity-40"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogOut className="w-4 h-4" />}
          Revoke all sessions
        </button>
      </div>

      {configured.length === 0 && (
        <p className="text-sm text-t3">
          No gated app is wired up for revoke on this panel (missing gate secret).
        </p>
      )}

      {confirming && (
        <div className="bg-warn-bg border border-warn-bd rounded-md px-4 py-3 space-y-3">
          <div className="flex items-start gap-2 text-sm text-t1">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-[#8B5206]" />
            <div className="space-y-1">
              <p className="font-medium">
                This signs out <span className="underline">everyone</span>, including you.
              </p>
              <p className="text-t2">
                Every open session in {configured.map((t) => t.label).join(', ')} ends
                at once. Your own is no exception — you’ll come back in through the
                panel, which takes one click. There is no per-user version of this:
                it is all sessions or none.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={revoke}
              className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-err text-white hover:brightness-110 disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogOut className="w-4 h-4" />}
              Yes, sign everyone out
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => setConfirming(false)}
              className="text-sm px-3 py-1.5 rounded-md border border-bd text-t2 hover:bg-s-2 disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {outcome?.error && (
        <div className="bg-err-bg border border-err-bd text-err text-sm rounded-md px-4 py-3">
          Nothing was revoked — {outcome.error}
        </div>
      )}

      {outcome?.results && (
        <div className="space-y-2">
          {outcome.results.map((r) => <RevokeResult key={r.key} result={r} />)}
        </div>
      )}
    </div>
  );
}

// One line per app. A failure is never dressed up as a success, and the
// "revoked but not persisted" case is its own state — sessions ARE dead, but a
// restart of that app would bring them back.
function RevokeResult({ result }) {
  const styles = {
    ok: 'bg-s-green border-bd text-t1',
    warn: 'bg-warn-bg border-warn-bd text-t1',
    err: 'bg-err-bg border-err-bd text-t1',
  };
  const MESSAGES = {
    revoked: 'All sessions ended.',
    revoked_not_persisted:
      'Sessions are ended, but the app could not save the change — restarting that app would bring every session back. Tell the operator before restarting it.',
    not_configured: 'Not attempted — the panel has no gate secret for this app.',
    rejected: 'Refused the panel’s token (403). The gate secret no longer matches — nothing was revoked.',
    replay: 'Refused as a replay (409) — a revoke already ran this second. Wait a moment and try again.',
    unreachable: 'Could not be reached — nothing was revoked.',
    failed: 'Answered unexpectedly — treat this as NOT revoked.',
  };

  const kind = result.outcome === 'revoked' ? 'ok'
    : result.outcome === 'revoked_not_persisted' ? 'warn' : 'err';
  const Icon = kind === 'ok' ? CheckCircle2 : kind === 'warn' ? AlertTriangle : XCircle;

  return (
    <div className={`flex items-start gap-2 text-sm rounded-md border px-3 py-2 ${styles[kind]}`}>
      <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${kind === 'ok' ? 'text-ok' : kind === 'warn' ? 'text-[#8B5206]' : 'text-err'}`} />
      <div>
        <span className="font-medium">{result.label}</span> — {MESSAGES[result.outcome] ?? result.outcome}
        {result.outcome === 'revoked' && result.previous_epoch != null && (
          <span className="text-t3"> (epoch {result.previous_epoch} → {result.epoch})</span>
        )}
        {result.detail && result.outcome !== 'revoked_not_persisted' && (
          <span className="block text-xs text-t3 mt-0.5">
            {result.http_status ? `HTTP ${result.http_status} — ` : ''}{result.detail}
          </span>
        )}
      </div>
    </div>
  );
}

function RoleBadge({ role }) {
  const styles = {
    owner: 'bg-warn-bg text-[#8B5206] border-warn-bd',
    admin: 'bg-s-blue text-blue border-bd-blue',
    member: 'bg-s-3 text-t2 border-bd',
  };
  const Icon = role === 'owner' ? Crown : role === 'admin' ? ShieldCheck : UserIcon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium border ${styles[role] ?? styles.member}`}>
      <Icon className="w-3.5 h-3.5" /> {ROLE_LABELS[role] ?? role}
    </span>
  );
}

function UserRow({ user, me, features, onChanged, onError }) {
  const [busy, setBusy] = useState(false);
  const [showAccess, setShowAccess] = useState(false);
  const isSelf = me?.email === user.email;
  const iAmOwner = me?.role === 'owner';
  // Only an owner can manage owner rows or promote to owner (mirrors backend).
  const canManageThisRow = user.role !== 'owner' || iAmOwner;
  const isMember = user.role === 'member';

  // The row keeps its key across the reload in onChanged(), so it stays
  // mounted — busy must be reset on success too, or every control on the row
  // stays disabled (with a spinner) until a full page reload.
  async function run(fn) {
    setBusy(true);
    try {
      await fn();
      onChanged();
    } catch (err) {
      onError(errMsg(err));
    } finally {
      setBusy(false);
    }
  }

  function changeRole(newRole) {
    if (newRole === user.role) return;
    if (newRole === 'owner' && !iAmOwner) return;
    if (!window.confirm(`Change ${user.email} from ${user.role} to ${newRole}?`)) return;
    run(() => api(`/api/users/${user.id}/role`, { method: 'PATCH', body: { role: newRole } }));
  }

  function toggleActive() {
    if (user.active) {
      if (!window.confirm(`Deactivate ${user.email}? They’ll be signed out and can’t log in until reactivated.`)) return;
      run(() => api(`/api/users/${user.id}/deactivate`, { method: 'POST' }));
    } else {
      run(() => api(`/api/users/${user.id}/reactivate`, { method: 'POST' }));
    }
  }

  function remove() {
    const typed = window.prompt(
      `Delete ${user.email}?\n\nThis removes the account permanently. Type the email to confirm:`,
    );
    if (typed === null) return;
    run(() => api(`/api/users/${user.id}`, { method: 'DELETE', body: { confirm_email: typed } }));
  }

  // Role options this actor may set on this row.
  const roleOptions = ['owner', 'admin', 'member'].filter((r) => (r === 'owner' ? iAmOwner : true));

  return (
    <>
    <tr className={`border-b border-bd ${showAccess ? '' : 'last:border-0'}`}>
      <td className="px-4 py-3">
        <span className="font-medium text-t1">{user.display_name || user.email}</span>
        {user.display_name && <span className="block text-xs text-t3">{user.email}</span>}
        {isSelf && <span className="ml-2 inline-block text-[10px] font-semibold uppercase text-t3">you</span>}
      </td>
      <td className="px-4 py-3">
        {canManageThisRow ? (
          <select
            value={user.role}
            disabled={busy}
            onChange={(e) => changeRole(e.target.value)}
            className="rounded-md border border-bd-strong bg-white px-2 py-1 text-xs disabled:opacity-50"
          >
            {roleOptions.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
          </select>
        ) : (
          <RoleBadge role={user.role} />
        )}
      </td>
      <td className="px-4 py-3">
        <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${
          user.active
            ? 'bg-s-green text-ok border-bd'
            : 'bg-s-3 text-t3 border-bd'
        }`}>
          {user.active ? 'Active' : 'Inactive'}
        </span>
      </td>
      <td className="px-4 py-3 text-t3 text-xs">
        {user.last_login_at ? new Date(`${user.last_login_at}Z`).toLocaleString() : '—'}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-1.5">
          {isMember && (
            <button
              type="button"
              onClick={() => setShowAccess((v) => !v)}
              className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border ${
                showAccess
                  ? 'border-bd-blue bg-s-blue text-blue'
                  : 'border-bd text-t2 hover:bg-s-2'
              }`}
            >
              <SlidersHorizontal className="w-3.5 h-3.5" /> Access
            </button>
          )}
          <button
            type="button"
            disabled={busy || isSelf || !canManageThisRow}
            onClick={toggleActive}
            title={isSelf ? 'You can’t deactivate yourself' : undefined}
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-bd text-t2 hover:bg-s-2 disabled:opacity-40"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Power className="w-3.5 h-3.5" />}
            {user.active ? 'Deactivate' : 'Reactivate'}
          </button>
          <button
            type="button"
            disabled={busy || isSelf || !canManageThisRow}
            onClick={remove}
            title={isSelf ? 'You can’t delete yourself' : undefined}
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-err-bd text-err hover:bg-err-bg disabled:opacity-40"
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
        </div>
      </td>
    </tr>
    {isMember && showAccess && (
      <tr className="border-b border-bd last:border-0 bg-s-2/60">
        <td colSpan={5} className="px-4 py-4">
          <PermissionsEditor
            user={user}
            features={features}
            disabled={!canManageThisRow}
            onSaved={onChanged}
            onError={onError}
          />
        </td>
      </tr>
    )}
    </>
  );
}

function PermissionsEditor({ user, features, disabled, onSaved, onError }) {
  const [perm, setPerm] = useState(() => ({
    features: { ...(user.permissions?.features ?? {}) },
  }));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false); // "Saved" tick — tells a save from a hang

  useEffect(() => {
    if (!saved) return undefined;
    const t = setTimeout(() => setSaved(false), 3000);
    return () => clearTimeout(t);
  }, [saved]);

  function toggleFeature(f) {
    setSaved(false);
    setPerm((p) => ({ ...p, features: { ...p.features, [f]: !p.features[f] } }));
  }

  // The editor stays mounted across onSaved()'s list reload (same row key), so
  // saving must be reset on success too — it used to reset only on error.
  async function save() {
    setSaving(true);
    setSaved(false);
    try {
      await api(`/api/users/${user.id}/permissions`, { method: 'PATCH', body: perm });
      setSaved(true);
      onSaved();
    } catch (err) {
      onError(errMsg(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-3">
      <div className="text-xs font-semibold text-t2 uppercase tracking-wide">
        Feature access for {user.email}
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
        {features.map((f) => (
          <label key={f} className="flex items-center gap-2 text-sm text-t2 bg-white border border-bd rounded-md px-2.5 py-1.5">
            <input
              type="checkbox"
              checked={perm.features[f] === true}
              disabled={disabled || saving}
              onChange={() => toggleFeature(f)}
              className="rounded border-bd-strong"
            />
            {FEATURE_LABELS[f] ?? f}
          </label>
        ))}
      </div>
      <p className="text-xs text-t3">
        A feature shows its page and lets them create there. What they see and can do inside it is set per resource below.
      </p>
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={disabled || saving}
          onClick={save}
          className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-blue text-white hover:bg-blue-deep disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <SlidersHorizontal className="w-4 h-4" />}
          Save access
        </button>
        {saved && (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-ok">
            <CheckCircle2 className="w-3.5 h-3.5" /> Saved
          </span>
        )}
        <span className="text-xs text-t3">
          Changes apply on their next request — no re-login needed.
        </span>
      </div>
      <ResourceAccessEditor user={user} disabled={disabled} onError={onError} />
    </div>
  );
}

// Per-resource access (2026-10-22). Children follow their parent: a server's
// mail, proxy and HTTPS; a cloud account's droplets; a storage account's buckets.
const ACCESS_GROUPS = [
  { label: 'Sites', types: [['wp_site', 'WordPress'], ['static_site', 'Static']] },
  { label: 'Servers', note: 'mail, proxy and HTTPS follow the server', types: [['server', 'Server']] },
  { label: 'Cloud Servers', note: 'droplets / linodes follow the account', types: [['cloud_account', 'Account']] },
  { label: 'DNS Manager', types: [['dns_account', 'Account']] },
  { label: 'Object Storage', note: 'buckets follow the account', types: [['storage_account', 'Account']] },
];
const LEVEL_LABELS = { view: 'View', use: 'Use', full: 'Full' };

function ResourceAccessEditor({ user, disabled, onError }) {
  const [data, setData] = useState(null); // { rows, resources, errors }
  const [busyKey, setBusyKey] = useState(null);

  const load = useCallback(async () => {
    try {
      setData(await api(`/api/users/${user.id}/access`));
    } catch (err) {
      onError(errMsg(err));
    }
  }, [user.id, onError]);

  useEffect(() => { load(); }, [load]);

  async function setLevel(type, resourceId, level) {
    const k = `${type}:${resourceId}`;
    setBusyKey(k);
    try {
      await api(`/api/users/${user.id}/access`, { method: 'PUT', body: { type, resource_id: resourceId, level } });
      await load();
    } catch (err) {
      onError(errMsg(err));
    } finally {
      setBusyKey(null);
    }
  }

  if (data === null) return <p className="text-sm text-t3">Loading resource access…</p>;

  return (
    <div className="space-y-3 pt-2 border-t border-bd">
      <div className="text-xs font-semibold text-t2 uppercase tracking-wide">Resources for {user.email}</div>
      <p className="text-xs text-t3">
        <span className="font-medium">View</span> = read ·{' '}
        <span className="font-medium">Use</span> = normal actions (backup, mail domain / mailbox, HTTPS, test) ·{' '}
        <span className="font-medium">Full</span> = delete, reveal passwords, restore, suspend / pause, power, deploy, proxy, edit a server.
        What they create is theirs at Full.
      </p>
      <div className="grid lg:grid-cols-2 gap-3">
        {ACCESS_GROUPS.map((g) => (
          <AccessGroup
            key={g.label}
            group={g}
            data={data}
            disabled={disabled}
            busyKey={busyKey}
            onSet={setLevel}
          />
        ))}
      </div>
    </div>
  );
}

function AccessGroup({ group, data, disabled, busyKey, onSet }) {
  const [type, setType] = useState(group.types[0][0]);
  const [pick, setPick] = useState('');
  const [newLevel, setNewLevel] = useState('view');
  const typeLabel = Object.fromEntries(group.types);
  const rows = data.rows.filter((r) => typeLabel[r.type]);
  const granted = new Set(rows.filter((r) => r.type === type).map((r) => r.resource_id));
  const catalog = data.resources?.[type];
  const choices = (catalog ?? []).filter((r) => !granted.has(r.id));

  return (
    <div className="bg-white border border-bd rounded-md p-3 space-y-2">
      <div className="text-sm font-medium text-t1">
        {group.label}
        {group.note && <span className="ml-1 text-xs font-normal text-t3">— {group.note}</span>}
      </div>
      {rows.length === 0 ? (
        <p className="text-xs text-t3">Nothing — they see none of these.</p>
      ) : (
        <ul className="space-y-1">
          {rows.map((r) => {
            const k = `${r.type}:${r.resource_id}`;
            return (
              <li key={k} className="flex items-center gap-2 text-sm">
                {group.types.length > 1 && (
                  <span className="text-[10px] uppercase text-t3 w-16 shrink-0">{typeLabel[r.type]}</span>
                )}
                <span className={`flex-1 truncate ${r.label ? 'text-t2' : 'text-t3 italic'}`} title={r.resource_id}>
                  {r.label ?? `${r.resource_id} (not listed)`}
                </span>
                <select
                  value={r.level}
                  disabled={disabled || busyKey === k}
                  onChange={(e) => onSet(r.type, r.resource_id, e.target.value)}
                  className="rounded-md border border-bd-strong bg-white px-2 py-0.5 text-xs disabled:opacity-50"
                >
                  {Object.entries(LEVEL_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  <option value="none">Remove</option>
                </select>
                {busyKey === k && <Loader2 className="w-3.5 h-3.5 animate-spin text-t3" />}
              </li>
            );
          })}
        </ul>
      )}
      <div className="flex flex-wrap items-center gap-1.5 pt-1">
        {group.types.length > 1 && (
          <select
            value={type}
            disabled={disabled}
            onChange={(e) => { setType(e.target.value); setPick(''); }}
            className="rounded-md border border-bd-strong bg-white px-2 py-1 text-xs"
          >
            {group.types.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        )}
        {catalog === null ? (
          <span className="text-xs text-err">Can’t list them now{data.errors?.[type] ? ` — ${data.errors[type]}` : ''}</span>
        ) : (
          <select
            value={pick}
            disabled={disabled || choices.length === 0}
            onChange={(e) => setPick(e.target.value)}
            className="flex-1 min-w-0 rounded-md border border-bd-strong bg-white px-2 py-1 text-xs"
          >
            <option value="">{choices.length ? 'Add one…' : 'Nothing left to add'}</option>
            {choices.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
          </select>
        )}
        <select
          value={newLevel}
          disabled={disabled}
          onChange={(e) => setNewLevel(e.target.value)}
          className="rounded-md border border-bd-strong bg-white px-2 py-1 text-xs"
        >
          {Object.entries(LEVEL_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <button
          type="button"
          disabled={disabled || !pick || busyKey !== null}
          onClick={() => { onSet(type, pick, newLevel); setPick(''); }}
          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-bd-blue bg-s-blue text-blue hover:brightness-95 disabled:opacity-40"
        >
          <Plus className="w-3.5 h-3.5" /> Add
        </button>
      </div>
    </div>
  );
}

function AddUserForm({ me, onCreated }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('admin');
  const [displayName, setDisplayName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const roleOptions = ['owner', 'admin', 'member'].filter((r) => (r === 'owner' ? me?.role === 'owner' : true));
  const canSave = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) && !saving;

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true); setError(null);
    try {
      await api('/api/users', {
        method: 'POST',
        body: { email: email.trim(), role, display_name: displayName.trim() || undefined },
      });
      onCreated();
    } catch (err) {
      setError(errMsg(err));
      setSaving(false);
    }
  }

  const inputCls = 'mt-1 w-full rounded-md border border-bd-strong px-2.5 py-1.5 text-sm';

  return (
    <form onSubmit={handleSave} className="bg-white border border-bd rounded-lg p-5 space-y-4">
      <div className="flex items-center gap-2 text-sm font-semibold text-t1">
        <UserPlus className="w-4 h-4 text-t3" /> Add user
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <label className="block sm:col-span-2">
          <span className="text-xs font-medium text-t2">Google email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="person@example.com"
            autoComplete="off"
            className={inputCls}
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-t2">Role</span>
          <select value={role} onChange={(e) => setRole(e.target.value)} className={`${inputCls} bg-white`}>
            {roleOptions.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
          </select>
        </label>
        <label className="block sm:col-span-3">
          <span className="text-xs font-medium text-t2">Display name (optional)</span>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Full name"
            className={inputCls}
          />
        </label>
      </div>

      {error && (
        <div className="text-sm rounded-md px-3 py-2 border bg-err-bg border-err-bd text-err">{error}</div>
      )}

      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={!canSave}
          className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-blue text-white hover:bg-blue-deep disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
          Add user
        </button>
        <span className="text-xs text-t3">
          No email is sent — they get access the next time they sign in with Google.
        </span>
      </div>
    </form>
  );
}
